import { Component, EventEmitter, OnInit, Output, inject } from '@angular/core';
import { MatDialog, MatDialogTitle, MatDialogContent, MatDialogActions, MatDialogRef } from '@angular/material/dialog';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { MatError, MatFormFieldModule, MatLabel } from '@angular/material/form-field';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';

import { ThemeService } from '~/core/services/theme.service';
import { AuthenticationService } from '~/core/services/authentication.service';
import { constants } from '~/core/constants/constants';
import { CardService } from '~/core/services/card.service';
import { MessageDialogComponent } from '../message-dialog/message-dialog.component';
import { CardDetailsDialogComponent } from '../card-details-dialog/card-details-dialog.component';
import ErrorParser from '../../../core/helpers/ErrorParser';
import { PrintingService } from '~/core/services/printing.service';
import { ApiErrorParser } from '~/core/helpers/api-error-parser';
import { CurrencyCasePipe } from '~/core/helpers/currency-case-pipe/currency-case-pipe';
import { FormErrorPipe } from '~/core/helpers/form-error.pipe';
import { NoAutofillDirective } from '~/core/helpers/no-autofill.directive';

@Component({
  selector: 'app-create-card-dialog',
  templateUrl: './create-card-dialog.component.html',
  styleUrls: ['./create-card-dialog.component.scss'],
  imports: [
    TranslatePipe,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    FormsModule,
    ReactiveFormsModule,
    CurrencyCasePipe,
    DatePipe,
    MatFormFieldModule,
    NoAutofillDirective,
    MatError,
    MatButton,
    MatIconButton,
    MatIcon,
    MatLabel,
    ReactiveFormsModule,
    FormErrorPipe,
    MatInput,
  ],
})
export class CreateCardDialogComponent implements OnInit {
  dialogRef = inject<MatDialogRef<CreateCardDialogComponent>>(MatDialogRef);
  themeService = inject(ThemeService);
  auth = inject(AuthenticationService);
  translate = inject(TranslateService);
  dialog = inject(MatDialog);
  fb = inject(UntypedFormBuilder);
  cardService = inject(CardService);
  printingService = inject(PrintingService);
  readonly apiErrorParser = inject(ApiErrorParser);

  form: UntypedFormGroup;
  data = {
    bettor: '',
    pin: null,
    value: null,
  };
  STATE: string;
  states = {
    CREATE: 'CREATE',
    SUCCESS: 'SUCCESS',
  };
  card;

  @Output() successfullyFunc = new EventEmitter();
  hidePin = true;
  hideConfirmPin = true;

  constructor() {}

  ngOnInit() {
    this.initForm();
    this.STATE = this.states.CREATE;
  }

  private initForm() {
    this.form = this.fb.group({
      bettor: this.fb.control('', [Validators.required, Validators.minLength(6)]),
      value: this.fb.control(
        {
          value: null,
          disabled: false,
        },
        [Validators.required, Validators.min(constants.BET_VALUES.MIN_BET_VALUE), Validators.max(constants.BET_VALUES.MAX_BET_VALUE)],
      ),
      pin: this.fb.control(
        {
          value: null,
          disabled: false,
        },
        [Validators.required, Validators.minLength(6), Validators.pattern('[0-9]+')],
      ),
      confirm_pin: this.fb.control(
        {
          value: null,
          disabled: false,
        },
        [Validators.required, Validators.minLength(6), Validators.pattern('[0-9]+')],
      ),
    });
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  get currentTheme() {
    return this.themeService.getTheme();
  }

  confirm() {
    if (this.form.valid) {
      let isValid = true;

      this.data.bettor = this.form.get('bettor').value;
      this.data.value = this.form.get('value').value;
      this.data.pin = this.form.get('pin').value;
      const confirmPin = this.form.get('confirm_pin').value;

      if (this.data.pin !== confirmPin) {
        this.form.controls.confirm_pin.setErrors({ incorrect: true });
        isValid = false;
      }

      if (this.data && isValid) {
        this.cardService.add(this.data).subscribe(
          (next) => {
            this.successfullyFunc.emit();
            this.STATE = this.states.SUCCESS;
            this.card = next;

            this.auth.refreshBalance();
          },
          (error) => {
            this.onNoClick();
            this.showError(error.error || error);
          },
        );
      }
    }
  }

  showError(error) {
    const instance = this.dialog.open(MessageDialogComponent, { disableClose: true });
    instance.componentInstance.title = 'CARD.CREATION_ERROR_TITLE';
    instance.componentInstance.message = this.apiErrorParser.parseError(error.code, error.info);
  }

  showCardDetails(card) {
    const instance = this.dialog.open(CardDetailsDialogComponent, { disableClose: true });
    instance.componentInstance.card = card;
  }

  hasError(formControlName: string): boolean {
    return this.form.get(formControlName).invalid;
  }
  getErrorMessage(formControlName: string) {
    const { errors } = this.form.get(formControlName);

    return ErrorParser(errors);
  }

  getCode() {
    if (this.card && this.card.code) {
      return `${this.card.code.substring(0, 3)}-${this.card.code.substring(3, 6)}-${this.card.code.substring(6, 9)}`;
    }
    return '';
  }

  printCard() {
    this.printingService.printCard(this.card);
  }
}
